import { Collisions, GameRandom, Inject, List, ModelEvents, Singleton } from '@denishussar/html5-game-engine';

import { GamePadModel } from './GamePadModel';
import { MiniGameModel, MiniGameTypeEnum } from './MiniGameModel';
import { PlayerModel } from './PlayerModel';
import { RoomModel } from './RoomModel';
import { UnitModel } from './UnitModel';
import { RopesModel } from './MiniGames/RopesModel';
import { LaserModel } from './MiniGames/LaserModel';
import { RescueModel } from './MiniGames/RescueModel';
import { FuelModel } from './MiniGames/FuelModel';
import { EnginePowerUpModel } from './MiniGames/PowerEnigineUpModel';
import { PropsModel } from './MiniGames/PropsModel';
import { DisplayObject, Graphics } from 'pixi.js';

export enum wasdEnum {
    w,
    a,
    s,
    d
}

export enum AppStateEnum {
    none,
    afterloader,
    menu,
    game,
    character,
    pause,
    end
}

export enum GameStateEnum {
    none,
    quest,
    minimap,
    info,
    task,
}

export enum QuestStateEnum {
    none,
    departure = 'departure',
    anchor = 'anchor',
    boat = 'boat',
    wheel = 'wheel',
    find_boat = 'find_boat',
}

export enum EndScreenTextEnum {
    none,
    txt1 = 'txt1',
    txt2 = 'txt2',
    txt3 = 'txt3',
    txt4 = 'txt4',
}

export enum DialogTextEnum {
    props = 'props',
    ropes = 'ropes',
    engine = 'engine',
    laser = 'laser',
    fuel = 'fuel',
    rescue = 'rescue'
}

export enum DialogTypeTextEnum {
    none = 'none',
    start = 'start',
    hint = 'hint',
    end = 'end'
}

@Singleton
export class GameModel {
    public wasdState: Int32Array = new Int32Array(4);

    public readonly gameWidth: number = 1442;
    public readonly gameHeight: number = 1922;

    public showFullscreen: boolean = false;
    public gameIsLoading: boolean = false;

    public showMiniGame: boolean = false;
    public showMiniMap: boolean = false;
    public showQuestScreen: boolean = false;

    public showTooltipScreen: boolean = false;
    public showTooltipScreenText: string = '';
    public showTooltipDone: boolean = false;
    public showHurryUpScreen: boolean = false;
    public showHurryUpTimer: number = 0;

    public showDialogScreen: boolean = false;
    public showDialogScreenCallback: () => void;
    public showDialogScreenText: { titleText: string, buttonText: string } = null;
    public showDialogRect: Graphics | null = null;
    public showEndType: EndScreenTextEnum = EndScreenTextEnum.none;
    public showEndVictory: boolean = false;

    public showTaskScreen: boolean = false;
    public foundTaskScreen: boolean = false;

    public showAfterloaderScreenNewPlayer: boolean = false;

    public muteState: boolean = false;

    public player: PlayerModel = null;
    public unitList = new List<UnitModel>();
    public roomList: List<RoomModel> = new List<RoomModel>();

    public gamePad: GamePadModel = new GamePadModel();
    public currMiniGameType: MiniGameTypeEnum;
    public currMiniGame: MiniGameModel;
    public doneMiniGames = new Array<MiniGameTypeEnum>();
    public failedMiniGames = new Array<MiniGameTypeEnum>();
    public playDoneSound: boolean = false;
    public playLooseSound: boolean = false;
    public pos: [number, number] = [0, 0];

    public scale: number = 0.8;

    public person: number = 3;
    public wear: number = 1;
    public hat: number = 2;

    // state
    public appState: AppStateEnum = AppStateEnum.none;
    public gameState: GameStateEnum = GameStateEnum.none;
    public questState: QuestStateEnum = QuestStateEnum.none;

    public ptsName: string = ''; // the participator name
    public ptsEmail: string = '';

    constructor(
        @Inject private _$random: GameRandom,
        @Inject private _$modelEvents: ModelEvents,
        @Inject private _$collisions: Collisions,
        // Minigame
        @Inject public RopesModel: RopesModel,
        @Inject public LaserModel: LaserModel,
        @Inject public RescueModel: RescueModel,
        @Inject public FuelModel: FuelModel,
        @Inject public EnginePowerUpModel: EnginePowerUpModel,
        @Inject public PropsModel: PropsModel,
    ) {
        this.reset();
    }

    public reset(): void {
        this.wasdState = new Int32Array(4);

        this.showMiniGame = false;
        this.showMiniMap = false;
        this.showQuestScreen = false;

        this.showTooltipScreen = false;
        this.showTooltipScreenText = '';

        this.showDialogScreen = false;
        this.showDialogScreenCallback = null;
        this.showDialogScreenText = null;

        this.showEndType = EndScreenTextEnum.none;
        this.showEndVictory = false;

        this.showTaskScreen = false;
        this.foundTaskScreen = false;

        if (!this.gamePad) {
            this.gamePad = new GamePadModel();
        } else {
            this.gamePad.clear();
        }

        this.currMiniGameType = MiniGameTypeEnum.none;
        this.currMiniGame = null;
        this.doneMiniGames = [];
        this.failedMiniGames = [];

        this.playDoneSound = false;
        this.pos = [0, 0];

        this.scale = 0.8;

        this.gameState = GameStateEnum.none;
        this.questState = QuestStateEnum.none;

        if (this.RopesModel) {
            this.RopesModel.clear();
        }
        if (this.LaserModel) {
            this.LaserModel.clear();
        }
        if (this.RescueModel) {
            this.RescueModel.clear();
        }
        if (this.FuelModel) {
            this.FuelModel.clear();
        }
        if (this.EnginePowerUpModel) {
            this.EnginePowerUpModel.clear();
        }
        if (this.PropsModel) {
            this.PropsModel.clear();
        }

        if (!this.player) {
            this.player = new PlayerModel();
        }

        if (!this.roomList) {
            this.roomList = new List<RoomModel>();
        } else {
            this.roomList.clear();
        }

        if (!this.unitList) {
            this.unitList = new List<UnitModel>();
        } else {
            this.unitList.forEach(o => {
                if (o.collision) {
                    this._$collisions.remove(o.collision);
                }
            });
            this.unitList.clear();
        }

        this.addUnit(this.player);
    }

    public addUnit(playerModel: PlayerModel | null): UnitModel {
        let unit = new UnitModel(this.unitList.count());
        unit.pos = [1200, 800];
        unit.width = 200;
        unit.height = 200;
        this.unitList.add(unit);

        unit.player = playerModel;

        unit.collision = this._$collisions.createCircle(0, 0, 40);

        unit.visible = true;
        unit.name = playerModel.name;
        unit.guid = playerModel.guid;

        return unit;
    }

    public addRoom(pos: [number, number], key: string): RoomModel {
        let model = new RoomModel(key);

        model.pos = pos;
        model.key = key;

        this.roomList.add(model);

        return model;
    }
}
